import { z } from "zod";
import { FEEDBACK_TYPES } from "./feedback.ts";
import { FEEDBACK_AREAS, FEEDBACK_SEVERITIES } from "./feedback-labels.ts";
import { FEEDBACK_GROUP_TITLE_MAX, issueTextFindingSchema } from "./feedback-triage.ts";

/**
 * Assisted triage (openspec change `feedback-triage-assist`, spec `feedback-triage`): a model's
 * first pass on a report (a suggested group or new group title, likely duplicates, the type, a
 * severity and an area) and drafted group text, each accepted by an operator. Instance console
 * only: nothing here reaches a tenant.
 */

/** The suggestion's one short reason (design D2). */
export const FEEDBACK_SUGGESTION_REASON_MAX = 200;
/** Likely duplicates a suggestion lists. */
export const FEEDBACK_SUGGESTION_DUPLICATES_MAX = 3;
/** Unsorted reports one "Suggest for Unsorted" passes (design D1). */
export const FEEDBACK_SUGGEST_BATCH_MAX = 50;

export const FEEDBACK_SUGGESTION_STATUSES = ["pending", "accepted", "changed", "rejected"] as const;
export type FeedbackSuggestionStatus = (typeof FEEDBACK_SUGGESTION_STATUSES)[number];

/** The fields an operator may change before accepting (named, never their text, in the audit). */
export const FEEDBACK_SUGGESTION_FIELDS = ["group", "type", "severity", "area"] as const;
export type FeedbackSuggestionField = (typeof FEEDBACK_SUGGESTION_FIELDS)[number];

const titleSchema = z.string().trim().min(1).max(FEEDBACK_GROUP_TITLE_MAX);

/**
 * The suggestion pass's output (design D2), as the model must answer it: candidates by their
 * index in the prompt, never an ID. Lengths and counts are not bounded here (an over-long answer
 * must not fail the pass): the server maps indexes back, drops anything out of range and bounds
 * the duplicates to {@link FEEDBACK_SUGGESTION_DUPLICATES_MAX} and the text to its limits.
 */
export const feedbackPassOutputSchema = z.object({
  group: z.union([z.object({ index: z.number().int() }), z.object({ new: z.string() })]),
  duplicates: z.array(z.number().int()),
  type: z.enum(FEEDBACK_TYPES),
  severity: z.enum(FEEDBACK_SEVERITIES),
  area: z.enum(FEEDBACK_AREAS),
  reason: z.string(),
});
export type FeedbackPassOutput = z.infer<typeof feedbackPassOutputSchema>;

/** A suggested or duplicate report as the console lists it: no description. */
const suggestedReportSchema = z.object({
  id: z.uuid(),
  type: z.enum(FEEDBACK_TYPES),
  tenantName: z.string(),
  createdAt: z.iso.datetime({ offset: true }),
});

/** A stored suggestion, as the report view shows it. */
export const feedbackSuggestionSchema = z.object({
  id: z.uuid(),
  reportId: z.uuid(),
  /** An open group, or null with `newGroupTitle` for a new one. */
  group: z
    .object({ id: z.uuid(), title: z.string(), type: z.enum(FEEDBACK_TYPES), reports: z.number() })
    .nullable(),
  newGroupTitle: z.string().nullable(),
  duplicates: z.array(suggestedReportSchema),
  type: z.enum(FEEDBACK_TYPES),
  severity: z.enum(FEEDBACK_SEVERITIES),
  area: z.enum(FEEDBACK_AREAS),
  reason: z.string(),
  modelRole: z.string(),
  model: z.string(),
  status: z.enum(FEEDBACK_SUGGESTION_STATUSES),
  createdAt: z.iso.datetime({ offset: true }),
});
export type FeedbackSuggestion = z.infer<typeof feedbackSuggestionSchema>;

/**
 * A report's assistant state: its latest suggestion, or why there is none (`opted_out`: the
 * report's organisation keeps its reports out of the assistant, design D6).
 */
export const feedbackAssistStateSchema = z.object({
  suggestion: feedbackSuggestionSchema.nullable(),
  optedOut: z.boolean(),
});
export type FeedbackAssistState = z.infer<typeof feedbackAssistStateSchema>;

/** Asks for a pass; `again` replaces a pending suggestion ("Suggest again"). */
export const suggestFeedbackSchema = z.strictObject({ again: z.boolean().default(false) });
export type SuggestFeedback = z.infer<typeof suggestFeedbackSchema>;

/** "Suggest for Unsorted": the inbox's current filter, Unsorted reports only (design D1). */
export const suggestFeedbackBatchSchema = z.strictObject({
  type: z.enum(FEEDBACK_TYPES).optional(),
  tenantId: z.uuid().optional(),
  build: z.string().max(64).optional(),
});
export type SuggestFeedbackBatch = z.infer<typeof suggestFeedbackBatchSchema>;

/** One line of the batch's streamed progress (NDJSON), then a final `done` line. */
export const feedbackBatchProgressSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("report"),
    reportId: z.uuid(),
    outcome: z.enum(["suggested", "cached", "opted_out", "failed", "budget"]),
  }),
  z.object({ kind: z.literal("done"), total: z.number().int().min(0) }),
]);
export type FeedbackBatchProgress = z.infer<typeof feedbackBatchProgressSchema>;

/**
 * An operator's decision. `accept` may carry the operator's changes (a recorded `changed`
 * outcome names the fields, never their values); `reject` changes nothing.
 */
export const feedbackSuggestionDecisionSchema = z.discriminatedUnion("decision", [
  z.strictObject({
    decision: z.literal("reject"),
    seen: z.iso.datetime({ offset: true }).optional(),
  }),
  z.strictObject({
    decision: z.literal("accept"),
    /** Another open group, or a new group's title, instead of the suggested one. */
    group: z
      .union([z.strictObject({ id: z.uuid() }), z.strictObject({ new: titleSchema })])
      .optional(),
    type: z.enum(FEEDBACK_TYPES).optional(),
    severity: z.enum(FEEDBACK_SEVERITIES).optional(),
    area: z.enum(FEEDBACK_AREAS).optional(),
    /** The suggestion's `createdAt` as shown: a suggestion passed again since is refused. */
    seen: z.iso.datetime({ offset: true }).optional(),
  }),
]);
export type FeedbackSuggestionDecision = z.infer<typeof feedbackSuggestionDecisionSchema>;

export const feedbackSuggestionDecidedSchema = z.object({
  status: z.enum(FEEDBACK_SUGGESTION_STATUSES),
  /** The group the report is now in. */
  groupId: z.uuid().nullable(),
});
export type FeedbackSuggestionDecided = z.infer<typeof feedbackSuggestionDecidedSchema>;

/**
 * A drafted group (design D4): fills the group page's fields for editing, nothing is saved.
 * `findings` is the issue preview's check on the draft, so a copied run shows before saving.
 */
export const feedbackGroupDraftSchema = z.object({
  title: z.string(),
  summary: z.string(),
  severity: z.enum(FEEDBACK_SEVERITIES),
  area: z.enum(FEEDBACK_AREAS),
  /** Reports the draft was written from. */
  used: z.number().int().min(0),
  /** Reports left out because their organisation keeps reports out of the assistant. */
  leftOut: z.number().int().min(0),
  findings: z.array(issueTextFindingSchema),
});
export type FeedbackGroupDraft = z.infer<typeof feedbackGroupDraftSchema>;

/**
 * The draft's model output (design D4): neutral product-level words, never quotes; bounded by
 * the server (title {@link FEEDBACK_GROUP_TITLE_MAX}, summary 1,500 characters).
 */
export const feedbackDraftOutputSchema = z.object({
  title: z.string(),
  summary: z.string(),
  severity: z.enum(FEEDBACK_SEVERITIES),
  area: z.enum(FEEDBACK_AREAS),
});
export type FeedbackDraftOutput = z.infer<typeof feedbackDraftOutputSchema>;

/**
 * Today's triage spend (UTC day, the instance's own Bedrock), the alert threshold and the
 * optional cap (design D5), shown next to the assistant's actions.
 */
export const feedbackAssistSpendSchema = z.object({
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  usd: z.number().min(0),
  alertUsd: z.number().min(0),
  /** Null: no cap (the default). */
  capUsd: z.number().min(0).nullable(),
  /** True once a set cap is reached: passes answer `429 triage_budget_reached`. */
  capped: z.boolean(),
});
export type FeedbackAssistSpend = z.infer<typeof feedbackAssistSpendSchema>;
